import { useNavigate, Link } from 'react-router-dom';
import { Lock, Shield, Users, Key, Package, CheckCircle, Zap, Check, ShieldCheck, Fingerprint, FileKey, Eye, Server, Bug, Inbox, Send, Globe, Tag, FolderKanban, BarChart3, Sparkles, Cookie } from 'lucide-react';

export default function LandingPage() {
  const navigate = useNavigate();

  const plans = [
    {
      name: 'Starter',
      price: '0',
      period: 'Free Forever',
      description: 'Perfect for individuals and small teams',
      features: [
        'Up to 10 credentials',
        'Up to 5 certificates',
        '3 team members',
        'AES-256 encryption',
        'Role-based access',
        '2FA authentication',
        'Domain favicons',
        'Inbox & Sent tracking'
      ],
      cta: 'Get Started Free',
      popular: false
    },
    {
      name: 'Professional',
      price: '29',
      period: 'per month',
      description: 'Best for growing teams and businesses',
      features: [
        'Up to 100 credentials',
        'Up to 50 certificates',
        '20 team members',
        'Everything in Starter',
        'File attachments (PEM, PPK)',
        'Export to Excel',
        'Categories & Projects',
        'Priority support',
        'API access'
      ],
      cta: 'Start Free Trial',
      popular: true
    },
    {
      name: 'Enterprise',
      price: 'Custom',
      period: 'contact sales',
      description: 'For large organizations with advanced needs',
      features: [
        'Unlimited credentials',
        'Unlimited certificates',
        'Unlimited team members',
        'Everything in Professional',
        'Full audit logs access',
        'Custom plan limits',
        '24/7 dedicated support',
        'SLA guarantee',
        'On-premise deployment',
        'Custom training'
      ],
      cta: 'Contact Sales',
      popular: false
    }
  ];

  return (
    <div className="min-h-screen bg-background">
      <nav className="border-b border-gray-200 bg-white sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-6 py-4 flex justify-between items-center">
          <div className="flex items-center gap-2">
            <div className="bg-gradient-to-br from-primary to-primary-hover p-2 rounded-lg shadow-lg">
              <Package className="w-6 h-6 text-white" strokeWidth={2} />
            </div>
            <span className="font-manrope font-bold text-2xl text-text-primary tracking-tight">
              Keeboxs
            </span>
          </div>
          <button
            onClick={() => navigate('/auth')}
            className="btn-outline"
            data-testid="nav-signin-btn"
          >
            Sign In
          </button>
        </div>
      </nav>

      <div className="max-w-7xl mx-auto px-6">
        {/* Hero Section */}
        <section className="py-20 lg:py-32">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div>
              <div className="inline-block px-4 py-2 bg-primary/10 text-primary rounded-full text-sm font-medium mb-6">
                Trusted by 10+ Corporations with 2000+ Users and 40,000+ secrets
              </div>
              <h1 className="font-manrope font-bold text-5xl lg:text-6xl text-text-primary leading-tight tracking-tight mb-6">
                Enterprise Credential Vault for Modern Teams
              </h1>
              <p className="text-lg text-text-secondary mb-8 leading-relaxed">
                Store, encrypt, and share credentials securely within your organization. Built with enterprise-grade encryption, audit logging, and role-based access control.
              </p>
              <div className="flex gap-4">
                <button
                  onClick={() => navigate('/auth')}
                  className="btn-primary text-lg"
                  data-testid="hero-get-started-btn"
                >
                  Get Started Free
                </button>
                <button
                  onClick={() => window.scrollTo({ top: 800, behavior: 'smooth' })}
                  className="btn-outline text-lg"
                >
                  View Pricing
                </button>
              </div>
              <p className="text-sm text-text-muted mt-4">
                ✓ No credit card required ✓ 14-day free trial ✓ Cancel anytime
              </p>
            </div>
            <div className="relative">
              <div className="bg-gradient-to-br from-primary/10 to-primary/5 border-2 border-primary/20 rounded-2xl p-12 aspect-square flex items-center justify-center shadow-xl">
                <div className="bg-gradient-to-br from-primary to-primary-hover p-8 rounded-2xl shadow-2xl">
                  <Package className="w-32 h-32 text-white" strokeWidth={1.5} />
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Features Section */}
        <section className="py-20">
          <h2 className="font-manrope font-bold text-3xl lg:text-4xl text-text-primary text-center mb-4 tracking-tight">
            Everything You Need to Secure Your Secrets
          </h2>
          <p className="text-center text-text-secondary mb-16 max-w-2xl mx-auto">
            Comprehensive credential and certificate management with enterprise features
          </p>
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            <div className="bg-white border-2 border-gray-200 rounded-xl p-6 card-hover" data-testid="feature-encryption">
              <div className="bg-primary/10 p-4 rounded-lg w-fit mb-4">
                <Shield className="w-8 h-8 text-primary" strokeWidth={1.5} />
              </div>
              <h3 className="font-manrope font-semibold text-xl text-text-primary mb-2">
                Military-Grade Encryption
              </h3>
              <p className="text-text-secondary">
                AES-256 encryption for all credentials with org-specific keys.
              </p>
            </div>

            <div className="bg-white border-2 border-gray-200 rounded-xl p-6 card-hover" data-testid="feature-sharing">
              <div className="bg-success/10 p-4 rounded-lg w-fit mb-4">
                <Users className="w-8 h-8 text-success" strokeWidth={1.5} />
              </div>
              <h3 className="font-manrope font-semibold text-xl text-text-primary mb-2">
                Team Collaboration
              </h3>
              <p className="text-text-secondary">
                Share credentials securely with granular role-based permissions. Unshare anytime.
              </p>
            </div>

            <div className="bg-white border-2 border-gray-200 rounded-xl p-6 card-hover" data-testid="feature-roles">
              <div className="bg-blue-100 p-4 rounded-lg w-fit mb-4">
                <Key className="w-8 h-8 text-blue-600" strokeWidth={1.5} />
              </div>
              <h3 className="font-manrope font-semibold text-xl text-text-primary mb-2">
                Role-Based Access
              </h3>
              <p className="text-text-secondary">
                Admin, Member, and Viewer roles with subscription plan-based feature restrictions.
              </p>
            </div>

            <div className="bg-white border-2 border-gray-200 rounded-xl p-6 card-hover">
              <div className="bg-purple-100 p-4 rounded-lg w-fit mb-4">
                <Zap className="w-8 h-8 text-purple-600" strokeWidth={1.5} />
              </div>
              <h3 className="font-manrope font-semibold text-xl text-text-primary mb-2">
                Audit Logging
              </h3>
              <p className="text-text-secondary">
                Track all access and modifications with comprehensive audit trails.
              </p>
            </div>

            <div className="bg-white border-2 border-gray-200 rounded-xl p-6 card-hover">
              <div className="bg-orange-100 p-4 rounded-lg w-fit mb-4">
                <Lock className="w-8 h-8 text-orange-600" strokeWidth={1.5} />
              </div>
              <h3 className="font-manrope font-semibold text-xl text-text-primary mb-2">
                Certificate Management
              </h3>
              <p className="text-text-secondary">
                Track SSL/TLS certificates with expiry alerts, providers, formats, and download options.
              </p>
            </div>

            <div className="bg-white border-2 border-gray-200 rounded-xl p-6 card-hover" data-testid="feature-organized">
              <div className="bg-green-100 p-4 rounded-lg w-fit mb-4">
                <CheckCircle className="w-8 h-8 text-green-600" strokeWidth={1.5} />
              </div>
              <h3 className="font-manrope font-semibold text-xl text-text-primary mb-2">
                File Attachments
              </h3>
              <p className="text-text-secondary">
                Store PEM files, PPK keys, and certificates securely with GridFS storage.
              </p>
            </div>

            <div className="bg-white border-2 border-gray-200 rounded-xl p-6 card-hover" data-testid="feature-inbox-sent">
              <div className="bg-indigo-100 p-4 rounded-lg w-fit mb-4">
                <Inbox className="w-8 h-8 text-indigo-600" strokeWidth={1.5} />
              </div>
              <h3 className="font-manrope font-semibold text-xl text-text-primary mb-2">
                Inbox & Sent Tracking
              </h3>
              <p className="text-text-secondary">
                Track what's shared with you in Inbox, and what you've shared with others in Sent.
              </p>
            </div>

            <div className="bg-white border-2 border-gray-200 rounded-xl p-6 card-hover" data-testid="feature-favicons">
              <div className="bg-teal-100 p-4 rounded-lg w-fit mb-4">
                <Globe className="w-8 h-8 text-teal-600" strokeWidth={1.5} />
              </div>
              <h3 className="font-manrope font-semibold text-xl text-text-primary mb-2">
                Smart Domain Icons
              </h3>
              <p className="text-text-secondary">
                Auto-detect service icons from URLs - instantly recognize Gmail, AWS, Stripe, and more.
              </p>
            </div>

            <div className="bg-white border-2 border-gray-200 rounded-xl p-6 card-hover" data-testid="feature-categories-projects">
              <div className="bg-amber-100 p-4 rounded-lg w-fit mb-4">
                <FolderKanban className="w-8 h-8 text-amber-600" strokeWidth={1.5} />
              </div>
              <h3 className="font-manrope font-semibold text-xl text-text-primary mb-2">
                Categories & Projects
              </h3>
              <p className="text-text-secondary">
                Organize credentials by custom categories and project groups for easy filtering.
              </p>
            </div>
          </div>
        </section>

        {/* What's New Section */}
        <section className="py-20" data-testid="whats-new-section">
          <div className="bg-gradient-to-br from-gray-950 to-gray-900 rounded-3xl p-10 lg:p-14">
            <div className="flex items-center gap-3 mb-8">
              <div className="bg-amber-500/10 p-2.5 rounded-lg">
                <Sparkles className="w-6 h-6 text-amber-400" />
              </div>
              <h2 className="font-manrope font-bold text-3xl text-white tracking-tight">What's New in v1.3.0</h2>
            </div>
            <div className="grid md:grid-cols-2 gap-4">
              {[
                { icon: BarChart3, title: 'Plan Usage Dashboard', desc: 'Visual progress bars show credential, certificate, and user quota usage at a glance.' },
                { icon: Inbox, title: 'Inbox & Sent', desc: 'Dedicated views for tracking items shared with you and items you\'ve shared with your team.' },
                { icon: Globe, title: 'Smart Domain Icons', desc: 'Credentials auto-display service logos (Gmail, AWS, Slack, etc.) fetched from domain favicons.' },
                { icon: FolderKanban, title: 'Projects & Categories', desc: 'Organize and filter credentials/certificates by custom categories and project groups.' },
                { icon: Key, title: 'Subscription Plan Limits', desc: 'Admins can set credential, certificate, and user limits per plan with feature gates.' },
                { icon: Shield, title: 'Unshare & RBAC', desc: 'Revoke shared access anytime. Viewer/Member/Admin roles enforce strict permissions.' }
              ].map((item) => (
                <div key={item.title} className="flex items-start gap-4 p-4 rounded-xl hover:bg-white/5 transition-colors">
                  <div className="bg-amber-500/10 p-2 rounded-lg flex-shrink-0">
                    <item.icon className="w-5 h-5 text-amber-400" />
                  </div>
                  <div>
                    <h3 className="font-semibold text-white text-sm mb-1">{item.title}</h3>
                    <p className="text-gray-400 text-sm leading-relaxed">{item.desc}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Pricing Section */}
        <section className="py-20" id="pricing">
          <h2 className="font-manrope font-bold text-3xl lg:text-4xl text-text-primary text-center mb-4 tracking-tight">
            Simple, Transparent Pricing
          </h2>
          <p className="text-center text-text-secondary mb-16 max-w-2xl mx-auto">
            Choose the plan that&apos;s right for your team. All plans include core security features.
          </p>
          
          <div className="grid md:grid-cols-3 gap-8 max-w-6xl mx-auto">
            {plans.map((plan) => (
              <div
                key={plan.name}
                className={`bg-white rounded-2xl p-8 relative ${
                  plan.popular
                    ? 'border-2 border-primary shadow-xl scale-105'
                    : 'border-2 border-gray-200'
                }`}
                data-testid={`pricing-plan-${plan.name.toLowerCase()}`}
              >
                {plan.popular && (
                  <div className="absolute -top-4 left-1/2 -translate-x-1/2">
                    <span className="bg-gradient-to-r from-primary to-primary-hover text-white px-4 py-1 rounded-full text-sm font-medium">
                      Most Popular
                    </span>
                  </div>
                )}
                
                <div className="text-center mb-6">
                  <h3 className="font-manrope font-bold text-2xl text-text-primary mb-2">
                    {plan.name}
                  </h3>
                  <p className="text-text-muted text-sm mb-4">{plan.description}</p>
                  <div className="flex items-baseline justify-center gap-2">
                    {plan.price === 'Custom' ? (
                      <span className="font-manrope font-bold text-4xl text-text-primary">
                        Custom
                      </span>
                    ) : (
                      <>
                        <span className="text-2xl text-text-muted">$</span>
                        <span className="font-manrope font-bold text-5xl text-text-primary">
                          {plan.price}
                        </span>
                      </>
                    )}
                  </div>
                  <p className="text-text-muted text-sm mt-2">{plan.period}</p>
                </div>

                <ul className="space-y-3 mb-8">
                  {plan.features.map((feature) => (
                    <li key={feature} className="flex items-start gap-3">
                      <Check className="w-5 h-5 text-primary flex-shrink-0 mt-0.5" />
                      <span className="text-text-secondary text-sm">{feature}</span>
                    </li>
                  ))}
                </ul>

                <button
                  onClick={() => navigate('/auth')}
                  className={`w-full py-3 rounded-lg font-medium transition-all ${
                    plan.popular
                      ? 'btn-primary'
                      : 'bg-white border-2 border-gray-200 text-text-primary hover:border-primary hover:bg-primary/5'
                  }`}
                  data-testid={`btn-select-${plan.name.toLowerCase()}`}
                >
                  {plan.cta}
                </button>
              </div>
            ))}
          </div>

          <p className="text-center text-text-muted text-sm mt-12">
            All plans include 14-day free trial. No credit card required for Starter plan.
          </p>
        </section>

        {/* CTA Section */}
        <section className="py-20 mb-20">
          <div className="bg-gradient-to-br from-primary to-primary-hover rounded-2xl p-12 lg:p-16 text-center shadow-2xl">
            <h2 className="font-manrope font-bold text-4xl lg:text-5xl text-white mb-6 tracking-tight">
              Start Securing Your Credentials Today
            </h2>
            <p className="text-white/90 text-lg mb-8 max-w-2xl mx-auto">
              Join teams who trust Keeboxs for their credential management needs.
            </p>
            <button
              onClick={() => navigate('/auth')}
              className="bg-white text-primary px-8 py-4 rounded-lg font-semibold text-lg hover:bg-gray-100 transition-colors shadow-lg"
              data-testid="cta-get-started-btn"
            >
              Get Started Free
            </button>
            <p className="text-white/80 text-sm mt-4">
              No credit card required • 14-day free trial
            </p>
          </div>
        </section>

        {/* Security & Trust Certifications */}
        <section className="py-20 bg-gray-950 rounded-3xl mx-4 mb-8" data-testid="security-section">
          <div className="max-w-7xl mx-auto px-6">
            <div className="text-center mb-12">
              <div className="inline-flex items-center gap-2 bg-green-500/10 text-green-500 border border-green-500/20 px-4 py-1.5 rounded-full text-sm font-medium mb-6">
                <ShieldCheck size={16} />
                OWASP Compliant Security
              </div>
              <h2 className="font-manrope font-bold text-4xl lg:text-5xl text-white mb-4 tracking-tight">
                Enterprise-Grade Security
              </h2>
              <p className="text-gray-400 text-lg max-w-2xl mx-auto">
                Built with defense-in-depth security architecture. Every layer is hardened to protect your sensitive data.
              </p>
            </div>

            {/* Security Features Grid */}
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-5 mb-16">
              {[
                { icon: Lock, title: 'AES-256 Encryption', desc: 'All credentials encrypted at rest with military-grade AES-256 Fernet encryption', badge: 'Active' },
                { icon: Fingerprint, title: 'Multi-Factor Authentication', desc: 'TOTP-based 2FA with authenticator app support for all user accounts', badge: 'Active' },
                { icon: Shield, title: 'Brute Force Protection', desc: 'Automatic account lockout after 5 failed attempts with 15-min cooldown', badge: 'Active' },
                { icon: FileKey, title: 'Password Policy Enforcement', desc: 'Minimum 8 chars with uppercase, lowercase, numbers, and special characters', badge: 'Enforced' },
                { icon: Server, title: 'HTTPS & Security Headers', desc: 'HSTS, X-Frame-Options, CSP, XSS-Protection, and no-cache headers', badge: 'Active' },
                { icon: Eye, title: 'Full Audit Trail', desc: 'Every credential access, change, and share event is logged and traceable', badge: 'Active' },
                { icon: Users, title: 'Tenant Isolation', desc: 'Organization-scoped data access with role-based permissions (RBAC)', badge: 'Active' },
                { icon: Bug, title: 'Rate Limiting', desc: 'API-level rate limiting prevents abuse and DDoS on all sensitive endpoints', badge: 'Active' },
                { icon: Key, title: 'Secure File Uploads', desc: 'File type validation, 10MB size limit, and restricted extensions only', badge: 'Active' },
              ].map((item) => (
                <div key={item.title} className="bg-gray-900 border border-gray-800 rounded-xl p-6 hover:border-green-500/30 transition-colors">
                  <div className="flex items-start justify-between mb-4">
                    <div className="bg-green-500/10 p-2.5 rounded-lg">
                      <item.icon className="w-5 h-5 text-green-400" />
                    </div>
                    <span className="text-[10px] px-2 py-0.5 rounded-full bg-green-500/10 text-green-400 font-semibold uppercase tracking-wider">
                      {item.badge}
                    </span>
                  </div>
                  <h3 className="text-white font-semibold mb-2">{item.title}</h3>
                  <p className="text-gray-500 text-sm leading-relaxed">{item.desc}</p>
                </div>
              ))}
            </div>

            {/* Trust Certifications Badges */}
            <div className="border-t border-gray-800 pt-12">
              <p className="text-center text-gray-500 text-sm uppercase tracking-wider mb-8">Security Standards & Compliance</p>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                {[
                  { label: 'OWASP Top 10', sub: 'Compliant', color: 'text-blue-400 bg-blue-400/10 border-blue-400/20' },
                  { label: 'SOC 2 Type II', sub: 'Ready', color: 'text-purple-400 bg-purple-400/10 border-purple-400/20' },
                  { label: 'GDPR', sub: 'Compliant', color: 'text-green-400 bg-green-400/10 border-green-400/20' },
                  { label: 'ISO 27001', sub: 'Aligned', color: 'text-amber-400 bg-amber-400/10 border-amber-400/20' },
                ].map((cert) => (
                  <div key={cert.label} className={`flex flex-col items-center justify-center py-5 px-4 rounded-xl border ${cert.color}`} data-testid={`trust-badge-${cert.label.toLowerCase().replace(/ /g, '-')}`}>
                    <ShieldCheck className="w-8 h-8 mb-2" />
                    <p className="font-bold text-sm">{cert.label}</p>
                    <p className="text-xs opacity-70 mt-0.5">{cert.sub}</p>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </section>
      </div>

      {/* Footer */}
      <footer className="border-t border-gray-200 bg-white py-12" data-testid="landing-footer">
        <div className="max-w-7xl mx-auto px-6">
          <div className="grid md:grid-cols-4 gap-8 mb-8">
            {/* Brand */}
            <div>
              <div className="flex items-center gap-2 mb-3">
                <div className="bg-gradient-to-br from-primary to-primary-hover p-2 rounded-lg">
                  <Package className="w-5 h-5 text-white" strokeWidth={2} />
                </div>
                <span className="font-manrope font-bold text-xl text-text-primary">Keeboxs</span>
              </div>
              <p className="text-text-muted text-sm leading-relaxed mb-3">
                Enterprise-grade credential and certificate management platform built for modern teams.
              </p>
              <a href="https://sysqua.com" target="_blank" rel="noopener noreferrer" className="text-primary text-sm font-medium hover:underline" data-testid="link-sysqua">
                Powered by Sysqua Solutions Private Limited
              </a>
            </div>

            {/* Get Started */}
            <div>
              <h4 className="font-semibold text-text-primary mb-3 text-sm">Get Started</h4>
              <div className="space-y-2.5">
                <Link to="/auth" className="block text-sm text-text-muted hover:text-primary transition-colors" data-testid="footer-signup">
                  Sign Up / Create Account
                </Link>
                <Link to="/auth" className="block text-sm text-text-muted hover:text-primary transition-colors" data-testid="footer-signin">
                  Sign In
                </Link>
                <Link to="/auth" className="block text-sm text-text-muted hover:text-primary transition-colors" data-testid="footer-demo">
                  Try Demo
                </Link>
              </div>
            </div>

            {/* Legal */}
            <div>
              <h4 className="font-semibold text-text-primary mb-3 text-sm">Legal</h4>
              <div className="space-y-2.5">
                <Link to="/privacy" className="block text-sm text-text-muted hover:text-primary transition-colors" data-testid="footer-privacy">
                  Privacy Policy
                </Link>
                <Link to="/terms" className="block text-sm text-text-muted hover:text-primary transition-colors" data-testid="footer-terms">
                  Terms of Service
                </Link>
                <button
                  onClick={() => { localStorage.removeItem('cookie_consent'); window.location.reload(); }}
                  className="block text-sm text-text-muted hover:text-primary transition-colors text-left"
                  data-testid="footer-cookies"
                >
                  Cookie Preferences
                </button>
              </div>
            </div>

            {/* Contact & Product */}
            <div>
              <h4 className="font-semibold text-text-primary mb-3 text-sm">Contact &amp; Support</h4>
              <div className="space-y-2 text-sm text-text-muted">
                <p>support@keeboxs.com</p>
                <p>support@sysqua.com</p>
                <a href="https://sysqua.com" target="_blank" rel="noopener noreferrer" className="block hover:text-primary transition-colors">sysqua.com</a>
              </div>
              <div className="mt-4 space-y-1 text-sm text-text-muted">
                <p>Version 1.3.0</p>
                <p>Released: 09-Jul-2026</p>
              </div>
            </div>
          </div>

          <div className="border-t border-gray-200 pt-6 flex flex-col sm:flex-row items-center justify-between gap-3">
            <p className="text-text-muted text-xs">
              &copy; 2026 Sysqua Solutions Private Limited. All rights reserved.
            </p>
            <div className="flex items-center gap-4 text-xs text-text-muted">
              <Link to="/privacy" className="hover:text-primary transition-colors">Privacy</Link>
              <Link to="/terms" className="hover:text-primary transition-colors">Terms</Link>
              <button onClick={() => { localStorage.removeItem('cookie_consent'); window.location.reload(); }} className="hover:text-primary transition-colors">Cookies</button>
            </div>
          </div>
        </div>
      </footer>
    </div>
  );
}
